/**
 * Concurrency Control Utilities
 * Prevents overwhelming database connections and API rate limits
 */
import { logger } from '@/utils/logger';

/**
 * Process items with controlled concurrency to prevent overwhelming services
 * @param items Array of items to process
 * @param processor Function to process each item (returns Promise)
 * @param concurrencyLimit Maximum number of concurrent operations (default: 5)
 * @param delayBetweenBatches Delay between batches in ms (default: 100)
 */
export async function processWithControlledConcurrency<T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  concurrencyLimit: number = 5,
  delayBetweenBatches: number = 100
): Promise<R[]> {
  const results: R[] = [];
  
  logger.debug(`🎯 CONCURRENCY: Processing ${items.length} items with limit ${concurrencyLimit}`);
  
  for (let i = 0; i < items.length; i += concurrencyLimit) {
    const batch = items.slice(i, i + concurrencyLimit);
    
    logger.trace(`🎯 CONCURRENCY: Processing batch ${Math.floor(i / concurrencyLimit) + 1}/${Math.ceil(items.length / concurrencyLimit)} (${batch.length} items)`);
    
    // Process batch with controlled concurrency
    const batchPromises = batch.map((item, batchIndex) => 
      processor(item, i + batchIndex)
    );
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Add delay between batches (except for the last batch)
    if (i + concurrencyLimit < items.length && delayBetweenBatches > 0) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }
  }
  
  logger.debug(`✅ CONCURRENCY: Completed processing ${items.length} items`);
  return results;
}

/**
 * Process database operations with rate limiting
 * Specifically designed for database queries that could overwhelm connection pools
 */
export async function processDbOperationsWithControl<T, R>(
  items: T[],
  dbOperation: (item: T) => Promise<R>,
  options: {
    concurrencyLimit?: number;
    delayBetweenBatches?: number;
    batchSize?: number;
    operationName?: string;
  } = {}
): Promise<R[]> {
  const {
    concurrencyLimit = 3, // Lower default for DB operations
    delayBetweenBatches = 200, // Higher delay for DB operations  
    batchSize = concurrencyLimit,
    operationName = 'database operation'
  } = options;

  logger.debug(`📊 DB_CONCURRENCY: Starting ${operationName} for ${items.length} items`);
  
  return processWithControlledConcurrency(
    items,
    async (item, index) => {
      try {
        const result = await dbOperation(item);
        return result;
      } catch (error) {
        console.error(`❌ DB_CONCURRENCY: Failed ${operationName} for item ${index}:`, error);
        throw error;
      }
    },
    concurrencyLimit,
    delayBetweenBatches
  );
}

/**
 * Process API calls with rate limiting
 * For external APIs that have rate limits
 */
export async function processApiCallsWithControl<T, R>(
  items: T[],
  apiCall: (item: T) => Promise<R>,
  options: {
    concurrencyLimit?: number;
    delayBetweenBatches?: number;
    apiName?: string;
    retryOnFailure?: boolean;
    maxRetries?: number;
  } = {}
): Promise<R[]> {
  const {
    concurrencyLimit = 2, // Very conservative for external APIs
    delayBetweenBatches = 500, // Longer delay for API rate limits
    apiName = 'external API',
    retryOnFailure = true,
    maxRetries = 3
  } = options;

  console.log(`🌐 API_CONCURRENCY: Starting ${apiName} calls for ${items.length} items`);
  
  return processWithControlledConcurrency(
    items,
    async (item, index) => {
      let lastError;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const result = await apiCall(item);
          if (attempt > 1) {
            console.log(`✅ API_CONCURRENCY: ${apiName} succeeded on attempt ${attempt} for item ${index}`);
          }
          return result;
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ API_CONCURRENCY: ${apiName} failed attempt ${attempt}/${maxRetries} for item ${index}:`, error);
          
          if (retryOnFailure && attempt < maxRetries) {
            // Exponential backoff for retries
            const retryDelay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }
      }
      
      console.error(`❌ API_CONCURRENCY: ${apiName} failed all ${maxRetries} attempts for item ${index}`);
      throw lastError;
    },
    concurrencyLimit,
    delayBetweenBatches
  );
}

/**
 * Batch processor with memory management
 * For operations that might consume significant memory
 */
export async function processBatchesWithMemoryControl<T, R>(
  items: T[],
  processor: (batch: T[]) => Promise<R[]>,
  options: {
    batchSize?: number;
    delayBetweenBatches?: number;
    operationName?: string;
    memoryThreshold?: number; // Memory usage threshold in MB
  } = {}
): Promise<R[]> {
  const {
    batchSize = 10,
    delayBetweenBatches = 100,
    operationName = 'batch operation',
    memoryThreshold = 500 // 500MB threshold
  } = options;

  const results: R[] = [];
  console.log(`🧠 MEMORY_CONCURRENCY: Starting ${operationName} for ${items.length} items in batches of ${batchSize}`);
  
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    
    // Check memory usage before processing
    if (typeof process !== 'undefined' && process.memoryUsage) {
      const memUsage = process.memoryUsage();
      const memUsageMB = memUsage.heapUsed / 1024 / 1024;
      
      if (memUsageMB > memoryThreshold) {
        console.warn(`⚠️ MEMORY_CONCURRENCY: High memory usage (${memUsageMB.toFixed(1)}MB), forcing garbage collection`);
        if (global.gc) {
          global.gc();
        }
        // Additional delay when memory is high
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches * 2));
      }
    }
    
    console.log(`🧠 MEMORY_CONCURRENCY: Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(items.length / batchSize)}`);
    
    const batchResults = await processor(batch);
    results.push(...batchResults);
    
    // Delay between batches
    if (i + batchSize < items.length && delayBetweenBatches > 0) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }
  }
  
  console.log(`✅ MEMORY_CONCURRENCY: Completed ${operationName} for ${items.length} items`);
  return results;
}

/**
 * Legacy Promise.all replacement
 * Drop-in replacement for Promise.all with automatic concurrency control
 */
export async function promiseAllWithControl<T>(
  promises: Promise<T>[],
  concurrencyLimit: number = 5
): Promise<T[]> {
  console.log(`🔄 PROMISE_ALL_CONTROL: Processing ${promises.length} promises with limit ${concurrencyLimit}`);
  
  // Convert promises to functions that return promises (for delayed execution)
  const promiseFunctions = promises.map(promise => () => promise);
  
  return processWithControlledConcurrency(
    promiseFunctions,
    async (promiseFunc) => await promiseFunc(),
    concurrencyLimit,
    50 // Short delay for promise processing
  );
}
